import { getLatestDrop, getLatestDropPromo } from "../core/api/latestDrop";

import Box from "../components/Box";
import BrandHomepage from "../components/BrandHomepage";

import { LATEST_DROP, MENSWEAR, WOMENSWEAR, KIDS } from "../core/constants";
import Layout from "../components/Layout";
import Listings from "../components/Listings";

import PageContent from "../components/PageContent";
import PageContentBrand from "../components/PageContentBrand";
import { getBrand } from "../core/api/brand";
import { getCategoryPages } from "../core/api/categories";

import { getFooter } from "../core/api/core";

import { getMenu } from "../core/api/menu";
import { getCategory } from "../core/api/category";

const ROOT_CATEGORIES = [MENSWEAR, WOMENSWEAR, KIDS, "latest-drop"];

export default function ReskinnedListings({
  category,
  menuData,
  brandMenuData,
  globalData,
  latestDrop,
  isLatestDrop,
  brand,
}) {
  if (brand && !category) {
    return (
      <BrandHomepage
        brand={brand}
        navItems={menuData}
        brandMenuData={brandMenuData}
        globalData={globalData}
        latestDrop={latestDrop}
      />
    );
  }

  return (
    <Layout navItems={menuData} brand={brand} globalData={globalData}>
      {brand && category ? (
        <PageContentBrand brandMenuData={brandMenuData} brand={brand}>
          <Box pb={[50, null, 100, null]}>
            <Listings
              category={category}
              isLatestDrop={isLatestDrop}
              brand={brand}
            />
          </Box>
        </PageContentBrand>
      ) : (
        <PageContent>
          <Box pb={[50, null, 100, null]} pt={60}>
            <Listings
              category={category}
              isLatestDrop={isLatestDrop}
              brand={null}
            />
          </Box>
        </PageContent>
      )}
    </Layout>
  );
}

function inBrandContext(params) {
  // The first param is not in root categories, so it must
  // be a brand slug
  return !ROOT_CATEGORIES.includes(params.categories[0]);
}

export async function getStaticProps({ params }) {
  const menuData = await getMenu();
  const globalData = await getFooter();

  // Brand homepage - only one 'params.categories' and it's not a root category
  // Latest drop - any of the 'params.categories' are latest drop
  // Category - anything else

  if (params.categories.length === 1 && inBrandContext(params)) {
    // It's the brand homepage
    const brandSlug = params.categories[0];
    const brand = await getBrand(brandSlug);
    // If the first param was not a root category or a brand then
    // return 404
    if (!brand) return { notFound: true };
    const brandMenuData = await getMenu(brandSlug);
    if (!brandMenuData) return { notFound: true };
    const latestDrop = await getLatestDropPromo(brandSlug);
    return {
      props: {
        menuData,
        brandMenuData,
        brand,
        latestDrop,
        globalData,
      },
      revalidate: 300,
    };
  }
  if (params.categories.includes(LATEST_DROP)) {
    // It's a latest drop - global or branded
    let brandSlug = null;
    let brandMenuData = null;
    let brand = null;
    if (inBrandContext(params)) {
      brandSlug = params.categories[0];
      brand = await getBrand(brandSlug);
      if (!brand) return { notFound: true };
      brandMenuData = await getMenu(brandSlug);
    }
    const category = await getLatestDrop(brandSlug, []);
    return {
      props: {
        menuData,
        brandMenuData,
        brand,
        category,
        isLatestDrop: true,
        globalData,
      },
      revalidate: 300,
    };
  }
  // If not brand homepage or latest drop then it must be a regular category
  // page - which could be global or branded
  //
  const categories = [...params.categories];
  const brandSlug = inBrandContext(params) ? categories.shift() : null;

  const brand = brandSlug ? await getBrand(brandSlug) : null;
  if (brandSlug && !brand) return { notFound: true };

  const brandMenuData = brandSlug ? await getMenu(brandSlug) : null;
  if (brandSlug && !brandMenuData) return { notFound: true };

  const rootCategorySlug = categories.shift();
  const categorySlug =
    categories.length === 0 ? null : [...categories].join("/");

  const category = await getCategory(rootCategorySlug, categorySlug, brandSlug);
  if (categorySlug && !category) return { notFound: true };

  return {
    props: {
      menuData,
      brandMenuData,
      globalData,
      category,
      brand,
      rootCategorySlug,
    },
    revalidate: 300,
  };
}

export async function getStaticPaths() {
  const categorySlugs = await getCategoryPages();

  return {
    paths: categorySlugs.map((categorySlugParts) => ({
      params: {
        categories: categorySlugParts,
      },
    })),

    fallback: "blocking",
  };
}
